import React, { useState, useEffect } from "react";
import Icons from "@/components/Icons";
import LoadingOverlay from "@/components/LoadingOverlay";
import FootnoteContent from "@/components/FootnoteContent";
import type { SaveCardProps, GameData } from "@/interfaces";
import { getTextSize } from "@/hooks/useGetTextSize";
import { getMyLocation, isUserInCalifornia } from "@/services/LocationService";
import { openCustomerPortal } from "@/lib/customer-portal-utils";
import { useStyleTheme } from "@/hooks/useStyleTheme";

const SafeTransactionInfo = ({
  savedCards = [],
  gameData,
  activeButton,
  isLoadingCards = false,
}: {
  savedCards?: SaveCardProps[];
  gameData?: GameData;
  activeButton?: "Credit Card" | "One Click" | "More Options";
  isLoadingCards?: boolean;
}) => {
  const textSize = getTextSize(gameData as GameData);
  const theme = useStyleTheme(gameData);
  const [isCaliforniaUser, setIsCaliforniaUser] = useState<boolean>(false);
  const [locationLoading, setLocationLoading] = useState<boolean>(true);
  const [isCustomerPortalLoading, setIsCustomerPortalLoading] = useState(false);

  const handleViewSavedCardsClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    await openCustomerPortal({
      gameData,
      onLoadingStart: () => setIsCustomerPortalLoading(true),
    });
  };

  // Fetch user location on component mount
  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        await getMyLocation();
        const inCalifornia = isUserInCalifornia();
        setIsCaliforniaUser(inCalifornia);
      } catch (error) {
        console.error('🚨 SafeTransactionInfo: Error fetching location:', error);
        setIsCaliforniaUser(false);
      } finally {
        setLocationLoading(false);
      }
    };

    fetchUserLocation();
  }, []);

  return (
    <>
      <LoadingOverlay 
        isVisible={isCustomerPortalLoading} 
        message="Opening saved cards..." 
        isAbsolute={false}
        theme={theme}
      />
      <section
        className={`flex flex-col items-center justify-end text-[#212121] font-normal ${textSize} leading-[100%] tracking-tightest px-8 relative min-h-max`}
      >
        <p className="flex items-center gap-2 my-2 landscape:my-1">
          <Icons type="shield" size={13} color={theme.colors.primary} />
          Safe & Encrypted Transaction
        </p>
        
        {activeButton === "Credit Card" && (
          <>
            {isLoadingCards ? (
              // Skeleton loading placeholder
              <div className="mb-1 portrait:mb-2 landscape:hidden">
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mx-auto"></div>
              </div>
            ) : savedCards.length > 0 ? (
              // Actual "View saved cards" link
              <a
                href="#"
                onClick={handleViewSavedCardsClick}
                className="underline cursor-pointer text-center mb-1 portrait:mb-2 landscape:hidden"
              >
                View saved cards
              </a>
            ) : null}
          </>
        )}
        
        <div className="landscape:hidden">
          <FootnoteContent
            gameData={gameData}
            isCaliforniaUser={locationLoading ? false : isCaliforniaUser}
            textClassName="text-center leading-snug"
            showPoweredBy={true}
            poweredByClassName="mb-2"
          />
        </div>
      </section>
    </>
  );
};

export default React.memo(SafeTransactionInfo);
